import { useState, useCallback, useRef } from 'react';
import { useCart, buildCartOrders } from '@/Context/CartContext';
import type { CartProductInput } from '@/Context/CartContext';
import api from '@/Services/api';
import { API } from '@/Services/apiConfig';

export interface MenuApiResponse {
    food_updates?: { id: number; daily_capacity: number; weight_percentage: number; available?: boolean; is_available?: boolean; price?: number | null }[];
    menu_updates?: number[];
}

export interface UseAddToBasketOptions {
    id: number;
    price: number;
    productName: string;
    isMenu?: boolean;
    weightPercentage?: number;
    portionLabel?: string;
}

export interface UseAddToBasketReturn {
    /** True while the component is performing a validate-then-add flow */
    isAdding: boolean;
    /** True while checking whether a specific date has capacity */
    isCheckingDate: boolean;
    /** Whether the last checked date was available for the requested quantity */
    dateIsAvailable: boolean;
    /**
     * Directly add an item to the cart (no API call).
     * Sets isAdding to false when done.
     */
    addToBasket: (qty: number, dateStr?: string | null) => void;
    /**
     * Check whether a given date + quantity is available via API.MENU.
     * Manages isCheckingDate / dateIsAvailable internally.
     * Returns true if available, false if not.
     * **Throws on network/server errors** so callers can implement
     * component-specific error handling (e.g. toast vs fallback add).
     */
    checkDateAvailability: (dateStr: string, qty: number) => Promise<boolean>;
    /** Allow the component to drive the isAdding flag around its own flow */
    setIsAdding: (adding: boolean) => void;
    setDateIsAvailable: (available: boolean) => void;
    setIsCheckingDate: (checking: boolean) => void;
}

export function useAddToBasket({
    id,
    price,
    productName,
    isMenu = false,
    weightPercentage,
    portionLabel,
}: UseAddToBasketOptions): UseAddToBasketReturn {
    const [isAdding, setIsAdding] = useState(false);
    const [isCheckingDate, setIsCheckingDate] = useState(false);
    const [dateIsAvailable, setDateIsAvailable] = useState(false);
    const { addToCart, cartItems } = useCart();
    
    // Store the validated price synchronously to avoid React state update lag
    const validatedPriceRef = useRef<number | null>(null);

    /** Build a CartProductInput and add it to the cart. Resets isAdding. */
    const addToBasket = useCallback(
        (qty: number, dateStr?: string | null) => {
            const date = dateStr ?? localStorage.getItem('selectedServiceDate') ?? '';
            const finalPrice = validatedPriceRef.current ?? price;
            if (finalPrice === null || finalPrice === undefined || isNaN(finalPrice)) {
                setIsAdding(false);
                return;
            }
            const payload: CartProductInput = {
                id,
                price: finalPrice,
                date,
                name: productName,
                weight_percentage: weightPercentage,
                portion_label: portionLabel,
            };
            if (isMenu) {
                payload.menu_id = id;
            }
            addToCart(payload, qty);
            setIsAdding(false);
            validatedPriceRef.current = null; // Clear ref after addition
        },
        [id, price, productName, weightPercentage, portionLabel, isMenu, addToCart],
    );

    /**
     * Check date availability via API.MENU.
     * Manages isCheckingDate / dateIsAvailable state.
     * Returns boolean for availability.
     * Throws on network/server errors so the caller can handle errors
     * in a component-specific way.
     */
    const checkDateAvailability = useCallback(
        async (dateStr: string, qty: number): Promise<boolean> => {
            setDateIsAvailable(false);
            setIsCheckingDate(true);
            try {
                const { data } = await api.post<MenuApiResponse>(API.MENU, {
                    date: dateStr,
                    cartOrders: buildCartOrders(cartItems),
                });

                let available = false;
                let priceFromApi: number | null = null;

                if (!isMenu && data.food_updates) {
                    const item = data.food_updates.find((f) => f.id === id);
                    available = !!item && (item.is_available ?? item.available ?? false) && item.daily_capacity >= qty;
                    if (available && item && item.price !== undefined && item.price !== null) {
                        priceFromApi = Number(item.price);
                    }
                } else if (isMenu && data.menu_updates) {
                    available = data.menu_updates.includes(id);
                }

                validatedPriceRef.current = priceFromApi;
                setDateIsAvailable(available);
                return available;
            } catch (err) {
                setDateIsAvailable(false);
                validatedPriceRef.current = null;
                throw err;
            } finally {
                setIsCheckingDate(false);
            }
        },
        [id, isMenu, cartItems],
    );

    return {
        isAdding,
        isCheckingDate,
        dateIsAvailable,
        addToBasket,
        checkDateAvailability,
        setIsAdding,
        setDateIsAvailable,
        setIsCheckingDate,
    };
}
